<template>
  <div class="container mt-4">
    <!-- État de chargement -->
    <div v-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
    </div>

    <!-- Erreur -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      {{ error }}
    </div>

    <!-- Contenu du profil -->
    <div v-else class="card shadow-sm">
      <div class="card-body">
        <h3 class="card-title mb-4">Mon Profil</h3>
        
        <div class="row">
          <!-- Photo de profil -->
          <div class="col-md-4 text-center">
            <div class="profile-picture-container mb-3">
              <img 
                :src="displayedProfilePicture" 
                alt="Photo de profil" 
                class="profile-picture"
              />
            </div>
            
            <!-- Input file caché -->
            <input 
              type="file" 
              ref="fileInput" 
              @change="handleFileSelect" 
              accept="image/*"
              style="display: none;"
            />
            
            <button 
              @click="$refs.fileInput.click()" 
              class="btn btn-outline-primary btn-sm mb-2"
              :disabled="uploadingPicture"
            >
              <span v-if="uploadingPicture" class="spinner-border spinner-border-sm" role="status"></span>
              <span v-else>📷 Changer la photo</span>
            </button>
            
            <!-- Bouton pour uploader après sélection -->
            <div v-if="selectedFile" class="mt-2">
              <p class="text-muted small">{{ selectedFile.name }}</p>
              <button @click="uploadProfilePicture" class="btn btn-success btn-sm me-2" :disabled="uploadingPicture">
                <span v-if="uploadingPicture" class="spinner-border spinner-border-sm" role="status"></span>
                <span v-else>✓ Valider</span>
              </button>
              <button @click="cancelFileSelect" class="btn btn-secondary btn-sm">
                ✕ Annuler
              </button>
            </div>
          </div>

          <!-- Informations du profil -->
          <div class="col-md-8">
            <!-- Nickname -->
            <div class="mb-4">
              <div class="d-flex align-items-center gap-2 mb-2">
                <h5 class="mb-0">Pseudo</h5>
                <button 
                  v-if="!editingNickname" 
                  @click="startEditNickname" 
                  class="btn btn-sm btn-outline-primary"
                >
                  ✏️ Modifier
                </button>
              </div>
              
              <!-- Affichage normal -->
              <p v-if="!editingNickname" class="lead">
                <strong>{{ profile.nickname }}</strong>
              </p>
              
              <!-- Mode édition -->
              <div v-else>
                <input 
                  v-model="newNickname" 
                  type="text" 
                  class="form-control mb-2" 
                  placeholder="Votre pseudo"
                  @keyup.enter="saveNickname"
                />
                <div class="d-flex gap-2">
                  <button @click="saveNickname" class="btn btn-sm btn-success" :disabled="savingNickname">
                    <span v-if="savingNickname" class="spinner-border spinner-border-sm" role="status"></span>
                    <span v-else>✓ Valider</span>
                  </button>
                  <button @click="cancelEditNickname" class="btn btn-sm btn-secondary">
                    ✕ Annuler
                  </button>
                </div>
              </div>
            </div>

            <!-- Email -->
            <div class="mb-4">
              <h5 class="mb-2">Email</h5>
              <p class="text-muted">
                <i class="bi bi-envelope"></i> {{ profile.email }}
              </p>
            </div>

            <!-- Informations supplémentaires -->
            <!-- <div class="mb-4">
              <h5 class="mb-2">Informations du compte</h5>
              <ul class="list-unstyled">
                <li class="mb-2">
                  <strong>Rôle :</strong> 
                  <span class="badge bg-primary">{{ profile.role }}</span>
                </li>
              </ul>
            </div> -->
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ProfileManager',
  data() {
    return {
      profile: null,
      loading: true,
      error: null,
      editingNickname: false,
      newNickname: '',
      savingNickname: false,
      defaultProfilePicture: 'https://via.placeholder.com/1000/4dc9ff/ffffff?text=Photo',
      selectedFile: null,
      previewUrl: null,
      uploadingPicture: false
    };
  },
  computed: {
    displayedProfilePicture() {
      if (this.previewUrl) {
        return this.previewUrl;
      }
      if (this.profile && this.profile.profilePicture) {
        return this.profile.profilePicture;
      }
      return this.defaultProfilePicture;
    }
  },
  mounted() {
    this.fetchProfile();
  },
  methods: {
    async fetchProfile() {
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        const response = await axios.get(`${API_URL}/api/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        this.profile = response.data;
        this.loading = false;
      } catch (err) {
        console.error('Erreur lors du chargement du profil:', err);
        this.error = 'Impossible de charger les informations du profil';
        this.loading = false;
      }
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Vérifier que c'est une image
      if (!file.type.startsWith('image/')) {
        alert('Veuillez sélectionner une image');
        return;
      }

      // Vérifier la taille (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('L\'image est trop grande (max 10MB)');
        return;
      }

      this.selectedFile = file;

      // Créer une prévisualisation
      const reader = new FileReader();
      reader.onload = (e) => {
        this.previewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    },

    cancelFileSelect() {
      this.selectedFile = null;
      this.previewUrl = null;
      this.$refs.fileInput.value = '';
    },

    async uploadProfilePicture() {
      if (!this.selectedFile) return;

      this.uploadingPicture = true;

      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('profilePicture', this.selectedFile);
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        const response = await axios.post(
          `${API_URL}/api/profile/upload-picture`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        // Mettre à jour l'URL de la photo
        this.profile.profilePicture = response.data.profilePicture;
        
        // Réinitialiser
        this.selectedFile = null;
        this.previewUrl = null;
        this.$refs.fileInput.value = '';

        // alert('Photo de profil mise à jour avec succès !');
        window.dispatchEvent(new Event('profile-picture-updated'));

      } catch (error) {
        console.error('Erreur lors de l\'upload:', error);
        alert('Impossible de télécharger la photo');
      } finally {
        this.uploadingPicture = false;
      }
    },
    
    startEditNickname() {
      this.newNickname = this.profile.nickname;
      this.editingNickname = true;
    },
    
    cancelEditNickname() {
      this.editingNickname = false;
      this.newNickname = '';
    },
    
    async saveNickname() {
      if (!this.newNickname.trim()) {
        alert('Le pseudo ne peut pas être vide');
        return;
      }
      
      this.savingNickname = true;
      
      try {
        const token = localStorage.getItem('token');
        const API_URL = process.env.VUE_APP_API_URL || 'https://brickfirst.fr';
        await axios.post(`${API_URL}/api/profile/update`, {
          nickname: this.newNickname
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        this.profile.nickname = this.newNickname;
        this.editingNickname = false;
        this.newNickname = '';
        
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error);
        alert('Impossible de modifier le pseudo');
      } finally {
        this.savingNickname = false;
      }
    }
  }
};
</script>

<style scoped>
.profile-picture-container {
  width: 200px;
  height: 200px;
  margin: 0 auto;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #4dc9ff;
  background-color: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.profile-picture {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lead {
  font-size: 1.5rem;
  font-weight: 400;
}

.gap-2 {
  gap: 0.5rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}
</style>